import React, { useMemo, useState } from "react";
import { Backdrop, Box, CircularProgress, Typography } from "@mui/material";
import { AddRounded } from "@mui/icons-material";

import PageTitle from "../../../components/PageTitle";
import { ChoiceChips, EmptyState, SearchField, useGatedNavigate } from "../../../components/ui";
import { SimpleSnackbar } from "../../../components/snackbar";
import { MessageDialog } from "../../../components/message-dialog";
import { ConfirmDeleteDialog } from "./modals/confirm-delete.dialog";
import DuplicateConsultDialog, { initialState } from "./modals/duplicate/duplicate-consult.dialog";
import useConsultationsHistory from "./use-consultations-history";
import { buildAlphabetIndex, getPatientInitialLetter, HistoryConsultation, HISTORY_SORT_OPTIONS } from "./history-filters";
import { useHistoryActions } from "./use-history-actions";
import PatientGroup from "./components/PatientGroup";
import ConsultationPreviewDialog from "./components/ConsultationPreviewDialog";
import AlphabetIndex from "./components/AlphabetIndex";

export const HistoryPage: React.FC = () => {
  const { navigateGated, gateDialogs } = useGatedNavigate();
  const { filtered, groups, loading, error, query, setQuery, sortOrder, setSortOrder, reload, deleteConsultation } = useConsultationsHistory();

  const {
    conflictDialog,
    setConflictDialog,
    duplicateConsultDialog,
    setDuplicateConsultDialog,
    deleteTarget,
    setDeleteTarget,
    deleteConsultDialog,
    setDeleteConsultDialog,
    showSnackbar,
    setShowSnackbar,
    snackbarMessage,
    pdfLoading,
    handlePdfGenerate,
    handleRecipe,
    handleDuplicate,
    onDuplicate,
    handleAskDelete,
    handleConfirmDelete,
    openConsultation,
    editConsultation,
  } = useHistoryActions({ reload, deleteConsultation, navigateGated });

  const alphabetIndex = useMemo(() => buildAlphabetIndex(groups), [groups]);
  const isAlphabetical = sortOrder === "alphabetical";
  const [previewItem, setPreviewItem] = useState<HistoryConsultation | null>(null);
  // Uma receita só com todas as fórmulas marcadas. O handlePdfGenerate já cuida
  // do conflito de ativos, do loading e do erro — é o mesmo caminho de uma consulta.
  const handleGenerateMany = (rows: HistoryConsultation[]) => {
    handlePdfGenerate(rows.map((row) => ({ id: row.id, date: row.date, name: row.focus, type: row.type })));
  };
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  const handleAlphabetSelect = (letter: string, patientId: number | null) => {
    if (!patientId) return;

    setActiveLetter(letter);
    document.getElementById(`history-patient-${patientId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const emptyStateNode = error ? (
    <EmptyState title="Erro ao carregar" description={error} actionLabel="Tentar novamente" onAction={reload} />
  ) : query ? (
    <EmptyState
      title="Nenhuma consulta encontrada"
      description="Tente ajustar a busca."
      actionLabel="Limpar busca"
      onAction={() => setQuery("")}
    />
  ) : (
    <EmptyState title="Nenhuma consulta ainda" description="As consultas realizadas aparecerão aqui." />
  );

  return (
    <Box sx={{ width: "100%", p: { xs: "18px 16px 24px", md: 4 } }}>
      <PageTitle title="Histórico" />

      {gateDialogs}

      <SimpleSnackbar show={showSnackbar} message={snackbarMessage} onDismiss={() => setShowSnackbar(false)} />

      <Backdrop open={pdfLoading} sx={{ zIndex: (t) => t.zIndex.drawer + 1, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {conflictDialog.show && (
        <MessageDialog
          open={conflictDialog.show}
          message={conflictDialog.message}
          onConfirm={() => handlePdfGenerate(conflictDialog.consults, true)}
          onClose={() => setConflictDialog({ show: false, message: "", consults: [] })}
          cancelButtonText="revisar minhas fórmulas"
          confirmButtonText="continuar mesmo assim"
        />
      )}

      <DuplicateConsultDialog {...duplicateConsultDialog} onDuplicate={onDuplicate} onClose={() => setDuplicateConsultDialog(initialState)} />

      {!!deleteTarget && deleteConsultDialog && (
        <ConfirmDeleteDialog
          open={deleteConsultDialog}
          resourceId={deleteTarget.id}
          resourceType={deleteTarget.type}
          onConfirm={(id, type) => handleConfirmDelete(id, type)}
          onClose={() => {
            setDeleteConsultDialog(false);
            setDeleteTarget(null);
          }}
          title="TEM CERTEZA QUE DESEJA REMOVER ESSA CONSULTA?"
          message="Essa ação é IRREVERSSÍVEL! E estes dados não poderão ser recuperados."
        />
      )}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "text.primary" }}>
          {!loading && groups.length > 0
            ? `${groups.length} ${groups.length === 1 ? "paciente" : "pacientes"} · ${filtered.length} ${filtered.length === 1 ? "consulta" : "consultas"}`
            : "Consultas"}
        </Typography>
        <Box
          component="button"
          onClick={() => navigateGated("/dashboard/query")}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 2,
            py: 1,
            borderRadius: "10px",
            border: "none",
            cursor: "pointer",
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            fontSize: "13.5px",
            fontWeight: 600,
            fontFamily: "inherit",
            "&:hover": { backgroundColor: "primary.dark" },
          }}
        >
          <AddRounded sx={{ fontSize: 17 }} />
          Nova consulta
        </Box>
      </Box>

      <Box sx={{ display: "grid", gap: 1.5, mb: 2.25 }}>
        <SearchField value={query} onChange={setQuery} placeholder="Buscar por paciente ou foco..." sx={{ maxWidth: 440 }} />

        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 1.25,
            p: { xs: 1.5, md: 1.75 },
            borderRadius: "20px",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            background: (theme) => `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary", mb: 0.5 }}>
              Ordenação
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "text.primary" }}>
              Escolha como organizar os pacientes no histórico.
            </Typography>
          </Box>

          <ChoiceChips options={HISTORY_SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} sx={{ maxWidth: "100%" }} />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} />
        </Box>
      ) : groups.length === 0 ? (
        emptyStateNode
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isAlphabetical ? { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) clamp(78px, 8.2vw, 102px)" } : "minmax(0, 1fr)",
            gap: { xs: 1.5, lg: 2 },
            alignItems: "start",
          }}
        >
          {isAlphabetical && <AlphabetIndex items={alphabetIndex} activeLetter={activeLetter} onSelect={(item) => handleAlphabetSelect(item.letter, item.patientId)} />}

          <Box
            sx={{
              order: { xs: 1, lg: 0 },
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 1fr)" },
              gap: { xs: 1.5, md: 1.75 },
              alignItems: "start",
            }}
          >
            {groups.map((group, index) => {
              const currentLetter = getPatientInitialLetter(group.patientName);
              const previousLetter = index > 0 ? getPatientInitialLetter(groups[index - 1].patientName) : null;
              const showLetterHeading = isAlphabetical && currentLetter && currentLetter !== previousLetter;

              return (
                <Box key={group.patientId} id={`history-patient-${group.patientId}`} sx={{ scrollMarginTop: { xs: 88, md: 104 } }}>
                  {showLetterHeading && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 44,
                        height: 44,
                        mb: 1,
                        px: 1.25,
                        borderRadius: "14px",
                        backgroundColor: "brand.surface2",
                        color: "brand.heroText",
                        fontSize: "18px",
                        fontWeight: 700,
                        boxShadow: "0 14px 30px rgba(18, 38, 63, 0.08)",
                      }}
                    >
                      {currentLetter}
                    </Box>
                  )}

                  <PatientGroup
                    group={group}
                    defaultExpanded={false}
                    onOpen={openConsultation}
                    onPreview={setPreviewItem}
                    onRecipe={handleRecipe}
                    onEdit={editConsultation}
                    onDuplicate={handleDuplicate}
                    onDelete={handleAskDelete}
                    onGenerateMany={handleGenerateMany}
                    generating={pdfLoading}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <ConsultationPreviewDialog
        item={previewItem}
        onClose={() => setPreviewItem(null)}
        onOpenFull={(item) => {
          setPreviewItem(null);
          openConsultation(item);
        }}
        onRecipe={handleRecipe}
      />
    </Box>
  );
};
